import React, { useState } from 'react';
import T from 'prop-types';
// utils
import { createEmptyTableData } from 'utils/misc';
import { isEmpty } from 'lodash';
import { alphabeticalSort, booleanSort, valueSort } from 'utils/sort';
import { simplifyPartName } from 'utils/string';
import { useSelector } from 'react-redux';
// components
import NoDataBanner from 'components/DismissBanner/NoDataBanner';
import Indicator from 'components/Indicator';
import Table from 'components/Table';
import ReloadButton from 'components/Button/ReloadButton';
import Typography from 'components/Typography';
import Modal from 'components/Modal';
// styles
import classnames from 'classnames/bind';
import styles from './PartProgressModal.module.scss';

const cx = classnames.bind(styles);

const studentRenderer = ({ first_name, last_name_initial, username }) => (
  <Typography className={cx('table-text')}>
    {!!first_name ? `${first_name} ${!!last_name_initial ? `${last_name_initial}.` : ''}` : username}
  </Typography>
);
const statusRenderer =
  (keys = []) =>
  ({ status }) =>
    status !== '--' ? (
      <Indicator checked={keys.includes(status)} />
    ) : (
      <Typography className={cx('table-text')}>{status}</Typography>
    );

const scoreRenderer = ({ assessment_score = null }) => (
  <Typography className={cx('table-text')}>{assessment_score ?? '--'}</Typography>
);

const columns = [
  {
    name: 'Student',
    width: 400,
    renderer: studentRenderer,
    sortFunction: alphabeticalSort(['first_name', 'username']),
  },
  {
    name: 'Started',
    width: 200,
    renderer: statusRenderer(['started', 'completed']),
    sortFunction: booleanSort(({ status }) => status === 'started' || status === 'completed'),
  },
  {
    name: 'Finished',
    width: 200,
    renderer: statusRenderer(['completed']),
    sortFunction: booleanSort(({ status }) => status === 'completed'),
  },
  {
    name: 'Score',
    width: 100,
    renderer: scoreRenderer,
    sortFunction: valueSort('assessment_score'),
  },
];

const PartProgressModal = ({ refetchData, progressTarget, hasData, onClose }) => {
  const [bannerOpen, setBannerOpen] = useState(false);
  const { partProgress = [], id: classId } = useSelector(({ classroom }) => classroom);
  const { remainingStudents = [] } = useSelector(({ user }) => {
    const currClass = (user.classrooms || []).find(({ id }) => +id === +classId);
    const partProgIds = partProgress.map(({ id }) => id);
    const remainingStudents = (currClass?.students || []).filter(({ id }) => !partProgIds.includes(id));

    return { remainingStudents };
  });

  const allStudents = [...partProgress, ...remainingStudents];

  return (
    <Modal
      className={cx('modal-wrapper', { translated: !hasData && bannerOpen })}
      isOpen={!isEmpty(progressTarget)}
      onClose={onClose}
    >
      {!hasData && <NoDataBanner onStateChange={setBannerOpen} className={cx('no-data-banner')} />}
      <div className={cx('container', 'pdv-50', 'pdh-55')}>
        <Typography type="body-s">Progress Details</Typography>
        <div className={cx('heading-wrapper', 'mgt-10', 'mgb-40')}>
          <ReloadButton variant="light" onClick={() => refetchData(progressTarget.id)} />
          <Typography type="h4" className="mgl-15">
            {simplifyPartName(progressTarget.title)}
          </Typography>
        </div>
        <Table
          columns={columns}
          data={
            allStudents.length
              ? allStudents
              : createEmptyTableData(['username', 'status', 'status', 'assessment_score'])
          }
        />
      </div>
    </Modal>
  );
};

PartProgressModal.propTypes = {
  refetchData: T.func.isRequired,
  progressTarget: T.object.isRequired,
  hasData: T.bool.isRequired,
  onClose: T.func.isRequired,
};

export default PartProgressModal;
